import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { getAllSignatures, UserKeys } from '../../../api/users-api';
import { SignatureModel } from '../../../../types';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useAnalytics } from '../../../shared/analytics/create-events';
import SignatureTemplatesGridToolbar from './signature-templates-grid-toolbar';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import DeleteSignatureTemplateDialog from './delete-signature-template-dialog';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { Messages } from '../../../utils/message-constants';
import { DisabledTooltipMenuItem } from '../../../shared/datagrids/tooltip-grid-actions-cell-item';
import RunSignatureTemplatesDialog from './run-signature-templates-dialog';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { useUserSettings } from '../../../utils/use-user-settings';
import { useNavigate } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import SharedDataGridClient from '../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import SignatureTemplateRunLogs from './signature-template-run-logs';
import Edit from '@mui/icons-material/Edit';
import LibraryAdd from '@mui/icons-material/LibraryAdd';
import List from '@mui/icons-material/List';
import Summarize from '@mui/icons-material/Summarize';
interface SignatureTemplatesGridProps {
  toolsRoles: string[];
  changeLogRoles: string[];
  onViewDetails: (template: SignatureModel) => void;
}

export default function SignatureTemplatesGrid({
  toolsRoles,
  changeLogRoles,
  onViewDetails,
}: SignatureTemplatesGridProps) {
  const { createEvent } = useAnalytics();
  const navigate = useNavigate();
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [openDeleteSignatureTemplate, setOpenDeleteSignatureTemplate] =
    useState(false);
  const [openRunAllTemplates, setOpenRunAllTemplates] = useState(false);

  const [selectedSignatureTemplate, setSelectedSignatureTemplate] =
    useState<SignatureModel>();
  const [openViewRunLogs, setOpenViewRunLogs] = useState(false);

  const {
    data: signatures,
    isLoading: signaturesLoading,
    isFetching: signaturesFetching,
  } = useQuery({
    queryKey: [UserKeys.Signatures],
    queryFn: () => getAllSignatures(),
    meta: {
      errorMessage:
        'There was an issue loading signature templates. Please refresh and try again.',
    },
  });

  const { viewType } = useUserSettings();

  const columns: GridColDef<SignatureModel>[] = useMemo(
    () => [
      {
        field: 'createdDate',
        headerName: 'Created Date',
        width: 180,
        type: 'dateTime',
        valueGetter: (value) => {
          const date = new Date(value);
          return date;
        },
        valueFormatter: (params: Date) => {
          const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'lastRunDate',
        headerName: 'Last Ran On',
        width: 180,
        type: 'dateTime',
        valueGetter: (value: number | null) => {
          if (!value) {
            return null;
          } else {
            const date = new Date(value);
            return date;
          }
        },
        valueFormatter: (params: Date | null) => {
          if (!params) {
            return 'Never';
          }
          const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
          return formattedDate;
        },
      },
      {
        field: 'templateName',
        headerName: 'Report Name',
        flex: 2,
      },
      {
        field: 'description',
        headerName: 'Description',
        flex: 2,
      },
      {
        field: 'manualRun',
        headerName: 'Manual Run',
        type: 'boolean',
        width: 120,
        valueGetter: (value: boolean) => {
          return value;
        },
      },
      {
        field: 'scheduled',
        headerName: 'Scheduled',
        type: 'boolean',
        width: 120,
        valueGetter: (value: boolean) => {
          return value;
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 120,
        getActions: (params) => {
          const canEdit = isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.EditTemplate.value
          );
          const canClone = isAbleToSeeUIElement(
            toolsRoles,
            ToolsRoles.CreateTemplates.value
          );

          const buttonArray = [];
          buttonArray.push(
            <GridActionsCellItem
              key="edit"
              label="Edit"
              icon={<Edit />}
              onClick={() => {
                navigate({
                  pathname: `/${
                    pagesConfig.signatureTemplates.link
                  }/${pagesConfig.editSignatureTemplate.link.replace(
                    ':id',
                    params.row.id.toString()
                  )}`,
                });
              }}
              showInMenu
              disabled={!canEdit}
              data-disabled-reason={
                !canEdit
                  ? Messages.Errors.Permission.Acting(
                      ToolsRoles.EditTemplate.name
                    )
                  : undefined
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              key="clone"
              label="Clone"
              icon={<LibraryAdd />}
              onClick={() => {
                navigate({
                  pathname: `/${
                    pagesConfig.signatureTemplates.link
                  }/${pagesConfig.cloneSignatureTemplate.link.replace(
                    ':id',
                    params.row.id.toString()
                  )}`,
                });
              }}
              showInMenu
              disabled={!canClone}
              data-disabled-reason={
                !canClone
                  ? Messages.Errors.Permission.Acting(
                      ToolsRoles.CreateTemplates.name
                    )
                  : undefined
              }
            />
          );

          buttonArray.push(
            <GridActionsCellItem
              key="logs"
              label="Logs"
              icon={<List />}
              onClick={() => {
                setOpenViewRunLogs(true);
                setSelectedSignatureTemplate(params.row);
              }}
              showInMenu
            />
          );
          return buttonArray;
        },
      },
    ],
    [navigate, toolsRoles, setOpenViewRunLogs, onViewDetails]
  );

  const defaultGridState = {
    sorting: {
      sortModel: [
        {
          field: 'createdDate',
          sort: 'desc',
        },
      ],
    },
  };

  const toolbarProps = useMemo(
    () => ({
      toolsRoles,
      setOpenDeleteSignatureTemplate,
      selectedRows,
      setOpenRunAllTemplates,
    }),
    [
      toolsRoles,
      setOpenDeleteSignatureTemplate,
      selectedRows,
      setOpenRunAllTemplates,
    ]
  );

  return (
    <>
      <SharedDataGridClient
        gridKey={
          viewType.signatureTemplatesGridStates?.activeGridState || 'default'
        }
        slots={{
          toolbar: useToolbar(SignatureTemplatesGridToolbar, toolbarProps),
          ...createGridOverlays('Signature Templates', <Summarize />),
          baseMenuItem: DisabledTooltipMenuItem,
        }}
        rows={signatures || []}
        columns={columns}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        loading={signaturesLoading || signaturesFetching}
        checkboxSelection
        disableRowSelectionOnClick
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        gridStateKey="signatureTemplatesGridStates"
        defaultGridState={defaultGridState}
        onRowClick={(row) => {
          onViewDetails(row.row);
          createEvent(
            analytics.automation.signature_templates
              .view_signature_template_details,
            {
              module: ModuleList.modules.automation.name,
              section:
                ModuleList.modules.automation.sections.signatureTemplates,
            }
          );
        }}
      />

      {selectedRows.length > 0 && openDeleteSignatureTemplate && signatures ? (
        <DeleteSignatureTemplateDialog
          open={openDeleteSignatureTemplate}
          handleClose={() => {
            setOpenDeleteSignatureTemplate(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          signatures={signatures}
        />
      ) : null}
      {selectedRows.length > 0 && openRunAllTemplates ? (
        <RunSignatureTemplatesDialog
          open={openRunAllTemplates}
          handleClose={() => {
            setOpenRunAllTemplates(false);
          }}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
          signatures={signatures}
        />
      ) : null}

      {selectedSignatureTemplate && openViewRunLogs ? (
        <SignatureTemplateRunLogs
          open={openViewRunLogs}
          handleClose={() => {
            setOpenViewRunLogs(false);
            setSelectedSignatureTemplate(undefined);
          }}
          selectedSignatureTemplate={selectedSignatureTemplate}
        />
      ) : null}
    </>
  );
}
